import { MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import type { AgentLocation } from "@/contracts/brokerage";
import { 
  US_STATES, 
  BEST_FOR_OPTIONS, 
  BROKERAGE_SIZE_OPTIONS, 
  BROKERAGE_TYPE_OPTIONS,
  PRIORITIES_OPTIONS,
} from "@/contracts/brokerage";

interface BrokerageFiltersType {
  state?: string;
  radius_miles?: number;
  best_for?: string[];
  brokerage_size?: string;
  brokerage_type?: string;
  priorities?: string[];
}

interface BrokerageFiltersProps {
  filters: BrokerageFiltersType;
  agentLocation: AgentLocation | null;
  onFiltersChange: (filters: BrokerageFiltersType) => void;
  onUseMyLocation: () => void;
  isLoadingLocation?: boolean;
}

export function BrokerageFilters({
  filters,
  agentLocation,
  onFiltersChange,
  onUseMyLocation,
  isLoadingLocation = false,
}: BrokerageFiltersProps) {
  const handleStateChange = (value: string) => {
    onFiltersChange({ ...filters, state: value === 'all' ? undefined : value });
  };

  const handleRadiusChange = (value: string) => {
    onFiltersChange({ ...filters, radius_miles: value === 'statewide' ? undefined : parseInt(value) });
  };

  const handleBestForToggle = (value: string) => {
    const current = filters.best_for || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFiltersChange({ ...filters, best_for: updated.length > 0 ? updated : undefined });
  };

  const handleSizeChange = (value: string) => {
    onFiltersChange({ ...filters, brokerage_size: value === 'all' ? undefined : value });
  };

  const handleTypeChange = (value: string) => {
    onFiltersChange({ ...filters, brokerage_type: value === 'all' ? undefined : value });
  };

  const handlePriorityToggle = (value: string) => {
    const current = filters.priorities || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onFiltersChange({ ...filters, priorities: updated.length > 0 ? updated : undefined });
  };

  const clearFilters = () => {
    onFiltersChange({ state: filters.state });
  };

  const activeFilterCount = [
    filters.best_for?.length || 0,
    filters.brokerage_size ? 1 : 0,
    filters.brokerage_type ? 1 : 0,
    filters.priorities?.length || 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Location Section */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="h-4 w-4 text-blue-600" />
          <Label className="font-semibold text-blue-900">Your Location</Label>
        </div>

        <div className="space-y-3">
          <Select value={filters.state || 'all'} onValueChange={handleStateChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All States</SelectItem>
              {US_STATES.map((state) => (
                <SelectItem key={state.value} value={state.value}>
                  {state.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button 
            variant="outline" 
            size="sm" 
            className="w-full gap-2"
            onClick={onUseMyLocation}
            disabled={isLoadingLocation}
          >
            <MapPin className="h-4 w-4" />
            {isLoadingLocation ? 'Getting location...' : 'Use my location'}
          </Button>

          {agentLocation?.lat && agentLocation?.lng && (
            <Select value={filters.radius_miles?.toString() || 'statewide'} onValueChange={handleRadiusChange}>
              <SelectTrigger>
                <SelectValue placeholder="Search radius" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">Within 10 miles</SelectItem>
                <SelectItem value="25">Within 25 miles</SelectItem>
                <SelectItem value="50">Within 50 miles</SelectItem>
                <SelectItem value="100">Within 100 miles</SelectItem>
                <SelectItem value="statewide">Statewide</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <Separator />

      {/* Filter header */}
      <div className="flex items-center justify-between">
        <Label className="font-semibold">Filters</Label>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-auto py-1 px-2 text-xs">
            Clear all
            <X className="h-3 w-3 ml-1" />
          </Button>
        )}
      </div>

      {/* Best For */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Best For</Label>
        <div className="space-y-2">
          {BEST_FOR_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <Checkbox
                id={`best-for-${option.value}`}
                checked={filters.best_for?.includes(option.value) || false}
                onCheckedChange={() => handleBestForToggle(option.value)}
              />
              <label htmlFor={`best-for-${option.value}`} className="text-sm cursor-pointer">
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Brokerage Size */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Brokerage Size</Label>
        <Select value={filters.brokerage_size || 'all'} onValueChange={handleSizeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Any size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any size</SelectItem>
            {BROKERAGE_SIZE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Brokerage Type */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Office Type</Label>
        <Select value={filters.brokerage_type || 'all'} onValueChange={handleTypeChange}>
          <SelectTrigger>
            <SelectValue placeholder="Any type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any type</SelectItem>
            {BROKERAGE_TYPE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* What Matters */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">What Matters to You</Label>
        <div className="flex flex-wrap gap-2">
          {PRIORITIES_OPTIONS.map((option) => (
            <Badge
              key={option.value}
              variant={filters.priorities?.includes(option.value) ? "default" : "outline"}
              className="cursor-pointer hover:bg-primary/10"
              onClick={() => handlePriorityToggle(option.value)}
            >
              {option.label}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}
